<template>
  <component :is="!context.isMobile ? DropdownMenuItem : DrawerClose" as-child>
    <button
      ref="buttonRef"
      :data-emphasis-identifier="
        id || props.label.replace(/\s+/g, '-').toLowerCase()
      "
      type="button"
      :disabled="props.disabled"
      :class="[
        'group/item',
        'relative flex shrink-0 cursor-pointer select-none items-center gap-3 self-stretch rounded-md border-none px-3 outline-none',
        'text-left text-sm text-current',
        !context.isMobile ? 'py-1' : 'py-2',
        premium && !disabled && 'opacity-50',
        disabled && 'pointer-events-none cursor-not-allowed opacity-50',
        variant === 'default' &&
          'active:bg-menu-muted data-[highlighted]:bg-menu-muted',
        variant === 'accent' &&
          'bg-blue-500 active:bg-blue-600 data-[highlighted]:bg-blue-600',
        variant === 'danger' &&
          'active:bg-red-600 data-[highlighted]:bg-red-600',
        props.class,
      ]"
      @click="onClick"
    >
      <slot v-if="slots.icon" name="icon" />
      <component
        :is="iconMappingMicro[pending ? 'arrow-path' : icon]"
        v-else-if="icon"
        :class="[
          'size-4 shrink-0 transition-colors',
          variant === 'default' && 'text-menu-muted-foreground',
          variant === 'accent' && 'text-white',
          variant === 'danger' &&
            'text-menu-muted-foreground group-active/item:text-white group-data-[highlighted]/item:text-white',
        ]"
      />

      <span class="min-w-0 grow break-words">
        {{ props.label }}
      </span>

      <BoltIcon
        v-if="premium"
        :class="[
          'size-4 shrink-0 transition-colors',
          variant === 'default' && 'text-menu-muted-foreground',
          variant === 'accent' && 'text-white',
          variant === 'danger' &&
            'text-menu-muted-foreground group-data-[highlighted]/item:text-white',
        ]"
      />

      <DropIndicator :edge="draggable.edge" />
    </button>
  </component>
</template>

<script setup lang="ts">
import { DropdownMenuItem } from "reka-ui";
import { DrawerClose } from "vaul-vue";
import { ref } from "vue";

import BoltIcon from "@/icons/heroicons/bolt-micro.svg";
import {
  type MappedIconMicro,
  iconMappingMicro,
} from "@/icons/icon-mapping-micro";
import { useReorderableListItem } from "@/lib/pragmatic-dnd";
import DropIndicator from "@/lib/pragmatic-dnd/DropIndicator.vue";

import { inject } from "./context";

export type Variant = "default" | "accent" | "danger";

const context = inject();

const props = withDefaults(
  defineProps<{
    label: string;
    icon?: MappedIconMicro;
    premium?: boolean;
    disabled?: boolean;
    pending?: boolean;
    variant?: Variant;
    index?: number;
    id?: string;
    class?: string;
    onClick?: () => void;
  }>(),
  {
    variant: "default",
  }
);

const slots = defineSlots<{
  icon?(): unknown;
}>();

const buttonRef = ref<HTMLButtonElement>();

const draggable = useReorderableListItem({
  element: buttonRef,
  index: () => props.index ?? -1,
});
</script>
